import { ulid } from 'ulid';
import { z } from 'zod';
import {
  Currency,
  IsoDate,
  IsoDatetime,
  type PayrollAdjustmentId,
  PayrollRunId,
  PayslipId,
  UserId,
  type WorkEntryId,
} from './primitives.js';

export const PayType = z.enum(['monthly', 'daily', 'hourly']);
export type PayType = z.infer<typeof PayType>;

export const WorkEntryKind = z.enum([
  'regular',
  'overtime',
  'holiday',
  'sick',
  'vacation',
  'unpaid_absence',
]);
export type WorkEntryKind = z.infer<typeof WorkEntryKind>;

export const PayrollAdjustmentKind = z.enum(['bonus', 'tip', 'deduction', 'advance', 'other']);
export type PayrollAdjustmentKind = z.infer<typeof PayrollAdjustmentKind>;

export const PayrollRunState = z.enum(['draft', 'finalized', 'paid']);
export type PayrollRunState = z.infer<typeof PayrollRunState>;

// Basis points, 10_000 = ×1.0. Capped at ×5 so a typo (125000 instead of
// 12500) can't quietly multiply somebody's overtime by twelve.
const MultiplierBp = z.number().int().min(10000).max(50000);

export const PayoutChannel = z.enum(['agency', 'bank', 'cash']);
export type PayoutChannel = z.infer<typeof PayoutChannel>;

// How the salary is actually handed over. A housekeeper's 3,200 can be 1,500
// through an agency contract and the rest in cash, and a payslip showing one
// number matches neither the contract nor what she is handed.
export const PayoutLine = z.object({
  label: z.string().min(1).max(120),
  // Null means "whatever is left", so the owner never has to keep two numbers
  // in sync when the salary changes.
  amount_minor: z.number().int().nonnegative().nullable(),
  channel: PayoutChannel,
});
export type PayoutLine = z.infer<typeof PayoutLine>;

export const SetCompensationRequest = z.object({
  staff_user_id: UserId,
  pay_type: PayType,
  base_amount_minor: z.number().int().nonnegative(),
  currency: Currency,
  standard_minutes_per_day: z.number().int().min(60).max(1440).optional(),
  standard_days_per_month: z.number().int().min(1).max(31).optional(),
  overtime_multiplier_bp: MultiplierBp.optional(),
  holiday_multiplier_bp: MultiplierBp.optional(),
  payday_day_of_month: z.number().int().min(1).max(28).optional(),
  payout_lines: z.array(PayoutLine).optional(),
  // What the household pays a third party to employ this person — agency fee,
  // visa admin. Never a deduction: it does not touch net pay, it exists so the
  // true cost of a person can be stated.
  employer_cost_minor: z.number().int().nonnegative().optional(),
  employer_cost_label: z.string().max(120).optional(),
  effective_from: IsoDate.optional(),
  note: z.string().max(500).optional(),
});

export const CreateWorkEntryRequest = z.object({
  staff_user_id: UserId.optional(),
  work_date: IsoDate,
  kind: WorkEntryKind,
  // Entered as minutes so the API never has to parse "7.5". The web form
  // does hours × 60 before it posts. 16h/day ceiling is a sanity guard, not
  // a policy — nobody logs 40 hours in one day on purpose.
  minutes: z.number().int().min(1).max(960),
  note: z.string().max(500).optional(),
});

export const ListWorkEntriesQuery = z.object({
  staff_user_id: UserId.optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
});

export const CreateAdjustmentRequest = z.object({
  staff_user_id: UserId,
  kind: PayrollAdjustmentKind,
  amount_minor: z.number().int().positive(),
  currency: Currency,
  effective_date: IsoDate.optional(),
  note: z.string().max(500).optional(),
});

// Same `<prefix>_<ulid>` shape as every other id in the system; spelled out
// here because the shared prefix registry does not carry this table.
export const RecurringDeductionId = z
  .string()
  .regex(/^rcd_[0-9A-HJKMNP-TV-Z]{26}$/, { message: 'expected recurringDeduction id (rcd_…)' });
export type RecurringDeductionId = z.infer<typeof RecurringDeductionId>;

export function newRecurringDeductionId(): string {
  return `rcd_${ulid()}`;
}

// A deduction the household agreed once and then stops thinking about — rent
// for a room, a phone line. Entered as a one-off every month it eventually
// gets forgotten, and the person who notices is the one being paid.
export const CreateRecurringDeductionRequest = z.object({
  staff_user_id: UserId,
  // The owner's own words: "Accommodation", "Uniform". Free text rather than
  // an enum — what a household deducts is not knowable in advance.
  label: z.string().min(1).max(120),
  amount_minor: z.number().int().positive(),
  currency: Currency,
  starts_on: IsoDate,
  // Open-ended is the normal case: rent for a room has no agreed end date.
  ends_on: IsoDate.nullable().optional(),
  note: z.string().max(500).optional(),
});

export const PayrollPeriodQuery = z.object({
  period_start: IsoDate,
  period_end: IsoDate,
});

export const CreatePayrollRunRequest = z.object({
  period_start: IsoDate,
  period_end: IsoDate,
  label: z.string().min(1).max(120).optional(),
});

export const PayRunRequest = z.object({
  paid_via: z.string().max(120).optional(),
  note: z.string().max(500).optional(),
});

export const UpdatePayrollSettingsRequest = z.object({
  currency: Currency.optional(),
  payday_day_of_month: z.number().int().min(1).max(28).optional(),
  default_overtime_multiplier_bp: MultiplierBp.optional(),
  default_holiday_multiplier_bp: MultiplierBp.optional(),
  default_standard_minutes_per_day: z.number().int().min(60).max(1440).optional(),
  default_standard_days_per_month: z.number().int().min(1).max(31).optional(),
  // 0 = never auto-deduct, 10_000 = allowed to take the whole payslip.
  max_loan_deduction_bp: z.number().int().min(0).max(10000).optional(),
  roll_reimbursements_into_payroll: z.boolean().optional(),
  // End-of-service terms. Not law in a field — a household's own contracts
  // decide, and the app keeps the running total visible against whatever they
  // say. 0 qualifying days means it accrues from the first day.
  gratuity_days_per_year: z.number().int().min(0).max(60).optional(),
  gratuity_qualifying_days: z.number().int().min(0).max(3650).optional(),
});

export const SetLoanRepaymentPlanRequest = z.object({
  monthly_deduction_minor: z.number().int().positive().nullable(),
  auto_deduct: z.boolean().optional(),
  // Money handed over in March that both sides agreed starts coming back in
  // June. Without a start date the first payslip after the loan swallows a
  // repayment nobody expected yet. Null = repay from the next run.
  deduction_starts_on: IsoDate.nullable().optional(),
});

// --- Response shapes (documented contract; handlers return drizzle rows) ---

export const PayslipLine = z.object({
  id: PayslipId,
  staff_user_id: UserId,
  currency: Currency,
  base_minor: z.number().int(),
  overtime_minor: z.number().int(),
  overtime_minutes: z.number().int(),
  holiday_minor: z.number().int(),
  holiday_minutes: z.number().int(),
  bonus_minor: z.number().int(),
  reimbursement_minor: z.number().int(),
  gross_minor: z.number().int(),
  absence_deduction_minor: z.number().int(),
  adjustment_deduction_minor: z.number().int(),
  advance_deduction_minor: z.number().int(),
  loan_deduction_minor: z.number().int(),
  net_minor: z.number().int(),
  // Resolved against net pay, so the "whatever is left" line is a number by
  // the time anyone reads a payslip.
  payout_lines: z.array(PayoutLine.extend({ amount_minor: z.number().int() })).optional(),
});

export const ResolvedPayoutLine = PayoutLine.extend({ amount_minor: z.number().int() });
export type ResolvedPayoutLine = z.infer<typeof ResolvedPayoutLine>;

export const PayrollRunSummary = z.object({
  id: PayrollRunId,
  period_start: IsoDate,
  period_end: IsoDate,
  label: z.string(),
  state: PayrollRunState,
  currency: Currency,
  gross_minor: z.number().int(),
  loan_deduction_minor: z.number().int(),
  net_minor: z.number().int(),
  // What the month cost the household on top of net pay — agency fees and the
  // like. Sits beside net rather than inside it because it is not the staff's
  // money.
  employer_cost_minor: z.number().int().optional(),
  finalized_at: IsoDatetime.nullable(),
  paid_at: IsoDatetime.nullable(),
});

export type WorkEntryIdType = z.infer<typeof WorkEntryId>;
export type PayrollAdjustmentIdType = z.infer<typeof PayrollAdjustmentId>;
